import * as Sentry from '@sentry/react';
/**
 * tokenUtils.js
 *
 * Utility functions for JWT token validation.
 * Used by AuthContext and ProtectedRoute to enforce token expiry
 * globally across all protected pages.
 */

/**
 * Converts a base64url-encoded string to a regular base64 string.
 * JWTs use base64url encoding which replaces '+' with '-', '/' with '_',
 * and omits padding '=' characters. The native atob() function requires
 * standard base64, so we must convert before decoding.
 *
 * @param {string} base64url - The base64url-encoded string to convert.
 * @returns {string} The decoded string value.
 * @throws {TypeError} If base64url is not a non-empty string.
 */
const decodeBase64Url = (base64url) => {
    if (typeof base64url !== 'string' || base64url.length === 0) {
        throw new TypeError('decodeBase64Url requires a non-empty string');
    }

    // Replace base64url characters with standard base64 equivalents
    const base64 = base64url
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    // Pad to a multiple of 4 characters as required by base64 spec
    const paddingNeeded = (4 - (base64.length % 4)) % 4;
    const paddedBase64 = base64 + '='.repeat(paddingNeeded);

    return atob(paddedBase64);
};

/**
 * Checks whether a JWT token has expired.
 * Parses the token's payload to read the `exp` claim and compares
 * it against the current time.
 *
 * JWTs are base64url-encoded (RFC 4648), not standard base64, so
 * the payload must be converted before parsing.
 *
 * @param {string} token - The JWT access token to validate.
 * @returns {boolean} True if the token is missing, malformed, or expired.
 */
export const isTokenExpired = (token) => {
    if (!token) return true;
    try {
        const parts = token.split('.');
        // A valid JWT must have exactly 3 parts: header.payload.signature
        if (parts.length !== 3) return true;

        const payload = JSON.parse(decodeBase64Url(parts[1]));

        // Validate that exp exists and is a numeric Unix timestamp
        if (typeof payload.exp !== 'number') return true;

        return payload.exp * 1000 <= Date.now();
    } catch (catchError) {
        Sentry.captureException(catchError);
        // Any error during parsing (malformed token, invalid JSON, etc.)
        // means we cannot trust the token — treat it as expired
        return true;
    }
};
